'use client';

import React, { Fragment, useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { GroqImage } from '@/helpers/Image';

import { PortableText } from '@portabletext/react';
import {
  customRichTextComponent,
  richTextComponent,
} from '@/sanity/sanityPortableText';

import { CTAButton } from '@/shadcn/ui/button';
import logo from '@/public/logo-black.svg';
import type { PageAffiliateSchema } from '@/sanity/pages/PageAffiliateTemplate';
import lastBG from '@/public/affiliate-overview/last-bg.png';
import lastStatue from '@/public/affiliate-overview/last-statue.png';
import dotsBG from '@/public/affiliate-overview/dots-bg.png';
import stepCardBG from '@/public/affiliate-overview/step-card-bg.png';

import GradualBlur from '@/shadcn/GradualBlur';
import { cn } from '@/lib/utils';
import Countdown from '@/components/global/countdown/Countdown';
import Link from 'next/link';
import arrow from '@/public/global/dotted-arrow.svg';
import { PageAssetsSchema } from '@/sanity/pages/PageAssetsTemplate';
import bgDots from '@/public/assets-page/whatCardbg.png';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi,
} from '@/shadcn/ui/carousel';

export default function LastCtaAssets({ data }: { data: PageAssetsSchema }) {
  const ease = [0.25, 0.1, 0.25, 1] as const;

  return (
    <>
      <section
        className={cn(
          'relative w-full p-[1rem] bg-[#B4B4B4]/[15%] lg:p-[5rem]  overflow-clip',
          data.pageStyle === 'crypto' ? 'lg:h-[105rem]' : 'lg:h-[115rem]',
        )}
      >
        <div
          className={cn(
            'relative w-full text-center pt-[6.5rem] h-[82.5rem] overflow-clip  lg:h-[119.375rem] lg:pt-[12.25rem]',
            data.pageStyle === 'crypto' ? 'pb-[70rem]' : 'pb-[82rem]',
          )}
        >
          <div className="px-[2.625rem] flex flex-col gap-[1.25rem]">
            {data?.lastChip && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="mb-0 font-blacker italic text-[2rem] text-black leading-[1.5]"
              >
                {data.lastChip}
              </motion.p>
            )}

            {data?.lastText && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease, delay: 0.15 }}
                className="richtext mx-auto lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] z-[5] relative prose-h6:font-blacker prose-h6:italic prose-h6:text-[#103037] prose-h6:text-[2.25rem] prose-h6:mb-[2rem] prose-p:w-[min(63.625rem,100%)] prose-p:text-[2rem] prose-strong:!font-[600] prose-em:font-[400] prose-em:text-[#003828] prose-em:font-blacker prose-headings:font-geist prose-headings:font-[500] prose-headings:mb-[2rem]"
              >
                <PortableText
                  value={data.lastText}
                  components={richTextComponent}
                />
              </motion.div>
            )}
            {data?.lastCTA && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, ease, delay: 0.3 }}
                className="flex relative justify-center mt-[3rem] z-[5] lg:[&_.button]:!min-h-[5.75rem]"
              >
                <CTAButton data={data.lastCTA} />
              </motion.div>
            )}
          </div>
        </div>
        <Image
          src={lastBG}
          alt=""
          quality={100}
          className="absolute inset-0 pointer-events-none z-[1] mix-blend-exclusion"
        />

        {data.heroImage && (
          <Image
            src={data.heroImage}
            alt=""
            quality={100}
            className={cn(
              'absolute bottom-0 left-1/2  pointer-events-none z-[1] w-[63.875rem] lg:w-full ',
              data.pageStyle === 'crypto'
                ? 'max-w-[92.75rem] -translate-x-1/2'
                : 'max-w-[87.125rem] -translate-x-[46%]',
            )}
          />
        )}
        <div className=" left-0 absolute bottom-0 w-full bg-gradient-to-t h-[22.25rem] from-[#F4F4F4] to-[#F4F4F4]/0 z-[2]" />
      </section>
    </>
  );
}
